import { faTimes, faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { ProductImageModelType } from '@teinor/erp/types/company/product/productImage'
import { ProductImageLangModelType } from '@teinor/erp/types/company/product/productImageLang'
import { useCallback, useEffect, useState } from 'react'
import { productImageParseLangs } from '../function'
import { EditImageTy } from '../type'
import { ButtonRedSecondary, ButtonPrimary } from '../../../../../../../baseComponents/Button'
import { UserPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import { ModalOpenedTy } from '../../../../../../../baseComponents/Modal/types'
import ModalDeleteFilled from '../../../../../../../baseComponents/ModalsLayouts/ModalDeleteFilled'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import FormRenderer from '../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../../utils/globals/data'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import { valueOrDefaultValue } from '../../../../../../../utils/globals/valueOrDefaultValue'
import useLangData from '../../../../../../../utils/hooks/useLangData'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { getActiveLangs } from '../../../../../../../utils/reducers/getters'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'

interface ProductEditImageProps extends ImageOptionsLogicProps {
    userPermissions: UserPermissions
}

const ProductEditImage: React.FC<ProductEditImageProps> = ({ editImage, setEditImage, getImages, userPermissions }) => {
    const { t } = useTwinTranslation()
    const { deleteModal, setDeleteModal, handleDelete, handleUpdate, data, selectedLang, changeLang, changeValCurrentLang, otherData, changeOtherData } = useImageOptionsLogic({ editImage, setEditImage, getImages })
    const fields: FormRender<ProductImageModelType & ProductImageLangModelType> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                    value: selectedLang,
                    onChange: changeLang
                },
                {
                    name: 'is_main',
                    component: 'CheckboxMargin',
                    label: t('cover', 'Portada'),
                    className: 'mr-20',
                    checked: otherData?.is_main,
                    onChange: (value) => changeOtherData(value, 'is_main')
                },
                {
                    name: 'text_alt',
                    placeholder: t('alternativeText', 'Texto alternativo (alt)'),
                    component: 'Input',
                    lang: selectedLang,
                    value: data?.['text_alt']?.[selectedLang] ?? '',
                    onChange: (name) => changeValCurrentLang('text_alt', name),
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'image_caption',
                    placeholder: t('legend', 'Leyenda'),
                    component: 'Input',
                    value: data?.['image_caption']?.[selectedLang] ?? '',
                    onChange: (name) => changeValCurrentLang('image_caption', name),
                    lang: selectedLang,
                    className: returnClassOfSpanTemplate(2)
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                }
            ]
        }
    ]
    let parsedFields: FormRender<any> = []
    if (data) {
        parsedFields = valueOrDefaultValue(fields, userPermissions, data)
    }
    if (!data) {
        return (
            <div className='product_image_options w-350 items-center flex' onClick={(e) => e.stopPropagation()}>
                <LoadingSpinner />
            </div>
        )
    }
    return (
        <div className='product_image_options' onClick={(e) => e.stopPropagation()}>
            <div className=' flex justify-end'>
                <TwinIcon className='cursor-pointer' onClick={() => setEditImage(null)} icon={faTimes} />
            </div>
            <div>
                <FormRenderer sections={parsedFields} />
            </div>
            <div className='flex justify-between items-center mt-30'>
                <ButtonRedSecondary onClick={() => setDeleteModal(true)} type='button' className='image_options_button'>
                    <TwinIcon icon={faTrashCan} className='mr-4' />
                    <TwinTrans transKey='delete'>
                        Eliminar
                    </TwinTrans>
                </ButtonRedSecondary>
                <ButtonPrimary type='button' className='image_options_button' onClick={handleUpdate}>
                    <TwinTrans transKey='save'>
                        Guardar
                    </TwinTrans>
                </ButtonPrimary>
            </div>
            <a href={editImage.url} className='text-green-21 text-16 flex justify-center mt-30' target='_blank' rel="noreferrer">
                <TwinTrans transKey='downloadImage'>Descargar imagen</TwinTrans>
            </a>
            {deleteModal && <ModalDeleteFilled translations={{
                title: t('sureOfDeleteImage', '¿Seguro que quieres eliminar esta imagen?'),
                subtitle: t('onceDeletedNotRecover', 'Una vez eliminada no podrás recuperarla')
            }} opened={true} setOpened={setDeleteModal} onCancel={() => setDeleteModal(null)} onAccept={handleDelete} className='modal_delete_product_image'></ModalDeleteFilled>}
        </div>
    )
}

interface ImageOptionsLogicProps {
    editImage: ProductImageModelType
    setEditImage: (data: EditImageTy) => void
    getImages: () => Promise<void>
}

const useImageOptionsLogic = ({ editImage, setEditImage, getImages }: ImageOptionsLogicProps) => {
    const { changeLang, selectedLang, data, changeValCurrentLang, changeData } = useLangData({
        myData: null, fieldsToLang: ['text_alt', 'image_caption'], keyValue: 'LangId'
    })
    const [otherData, setOtherData] = useState<ProductImageModelType | null>(null)
    const [deleteModal, setDeleteModal] = useState<ModalOpenedTy>(null)

    const getImageLangData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/product/productImage/getProductImageInstance', { id: editImage.id })
        if (result) {
            changeData(result.ProductImageLangs)
            setOtherData(result)
        }
    }, [changeData, editImage])

    const changeOtherData = useCallback(async <K extends keyof ProductImageModelType>(data: ProductImageModelType[K], field: K) => {
        setOtherData((old) => {
            if (!old) {
                return old
            }
            return {
                ...old,
                [field]: data
            }
        })
    }, [setOtherData])

    const afterUpdate = useCallback(() => {
        setEditImage(null)
        getImages()
    }, [setEditImage, getImages])

    const handleDelete = useCallback(async () => {
        const result = await twinFetchPostText('/api/app/product/productImage/deleteProductImage', { id: editImage.id })
        if (result) {
            afterUpdate()
            return true
        }
        return false
    }, [editImage, afterUpdate])

    const handleUpdate = useCallback(async () => {
        let textAltLangs = ''
        let imageCaptionLangs = ''
        if (data) {
            textAltLangs = productImageParseLangs(data, 'text_alt')
            imageCaptionLangs = productImageParseLangs(data, 'image_caption')
        }
        const result = await twinFetchPostText('/api/app/product/productImage/updateProductImage', {
            ...otherData,
            textAltLangs,
            imageCaptionLangs
        })
        if (result) {
            afterUpdate()
        }
    }, [afterUpdate, data, otherData])

    useEffect(() => {
        getImageLangData()
    }, [editImage.id, getImageLangData])

    return { handleDelete, handleUpdate, deleteModal, setDeleteModal, data, changeLang, selectedLang, changeValCurrentLang, changeOtherData, otherData }
}

export default ProductEditImage